import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  EllipsisSpan,
  message,
  PopConfirm,
  Select,
  Table,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { useMemo, useState } from 'react';
import {
  apiV2AreaGetValveSelectPost,
  apiV2AreaList2Post,
  apiV2AreaStationValveDelPost,
  apiV2AreaStationValveLinkPost,
  apiV2AreaStationValveListPost,
} from '@maxtropy/device-customer-apis-v2';
import { Space } from 'antd';
import DeviceSelectModal from '@/pages/AreaConfig/ValveConfig/components/DeviceSelectModal';
import { DeviceItem } from '@/pages/AreaConfig/ValveConfig/types';
import styles from './index.module.scss';
import { useNavigate } from 'react-router-dom';

interface RelationRow {
  rowKey: string;
  id?: number;
  stationId?: number;
  valveIds: number[];
  valveNames?: string[];
}

const cloneRows = (rows: RelationRow[]): RelationRow[] =>
  rows.map(item => ({
    ...item,
    valveIds: [...item.valveIds],
    valveNames: item.valveNames ? [...item.valveNames] : undefined,
  }));

const StationValveConfiguration: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const routes = [{ name: '站房关联阀门配置' }];
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<RelationRow[]>([]);
  const [originRows, setOriginRows] = useState<RelationRow[]>([]);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRowId, setCurrentRowId] = useState<string>();
  const [currentSelectedDevices, setCurrentSelectedDevices] = useState<DeviceItem[]>([]);
  const [saveLoading, setSaveLoading] = useState(false);

  const { data: areaList } = useRequest(() => apiV2AreaList2Post().then(res => res.list ?? []));
  const { data: valveData } = useRequest(() => apiV2AreaGetValveSelectPost().then(res => res.list ?? []));
  const { loading: listLoading, refresh: refreshTable } = useRequest(async () => {
    const res = await apiV2AreaStationValveListPost({});
    const rows: RelationRow[] = (res.list ?? []).map(item => ({
      rowKey: `server-${item.id ?? Math.random()}`,
      id: item.id,
      stationId: item.stationInfo?.id,
      valveIds: (item.valveList ?? []).map(valve => valve.id).filter((id): id is number => id !== undefined),
      valveNames: (item.valveList ?? []).map(valve => valve.name ?? '--'),
    }));
    const nextRows = cloneRows(rows);
    setTableData(nextRows);
    setOriginRows(cloneRows(nextRows));
    setDeletedIds([]);
    return rows;
  });

  const stationOptions = useMemo(
    () =>
      (areaList ?? []).map(item => ({
        label: item.zoneName ?? '--',
        value: item.id as number,
      })),
    [areaList]
  );

  const valveOptions = useMemo(
    () =>
      Array.from(
        new Map(
          (valveData ?? []).map(item => [item.id as number, { label: item.name ?? '--', value: item.id as number }])
        ).values()
      ),
    [valveData]
  );

  const valveNameMap = useMemo(
    () => new Map((valveOptions ?? []).map(item => [item.value, item.label])),
    [valveOptions]
  );

  const handleAddRow = () => {
    if (!isEditing) return;
    setTableData(prev => [...prev, { rowKey: `tmp-${Date.now()}-${prev.length}`, valveIds: [] }]);
  };

  const openValveModal = (row: RelationRow) => {
    if (!isEditing) return;
    setCurrentRowId(row.rowKey);
    setCurrentSelectedDevices(
      row.valveIds.map((id, index) => ({
        id,
        name: row.valveNames?.[index] ?? (valveNameMap.get(id) as string | undefined),
      }))
    );
  };

  const handleDeviceSelectOk = (selectedDevices: DeviceItem[]) => {
    if (!currentRowId) return;
    const currentRow = tableData.find(item => item.rowKey === currentRowId);
    if (!currentRow?.stationId) {
      message.warning('请先选择站房');
      return;
    }
    const selectedValveIds = selectedDevices.map(item => item.id);
    const selectedNames = selectedDevices.map(item => item.name ?? `${item.id}`);
    setTableData(prev =>
      prev.map(item =>
        item.rowKey === currentRowId
          ? {
              ...item,
              valveIds: selectedValveIds,
              valveNames: selectedNames,
            }
          : item
      )
    );
    setCurrentRowId(undefined);
    setCurrentSelectedDevices([]);
  };

  const handleStationChange = (rowId: string, stationId?: number) => {
    if (!isEditing) return;
    setTableData(prev => prev.map(item => (item.rowKey === rowId ? { ...item, stationId } : item)));
  };

  const handleDelete = (record: RelationRow) => {
    if (!isEditing) return;
    if (record.id === undefined) {
      setTableData(prev => prev.filter(item => item.rowKey !== record.rowKey));
      return;
    }
    setDeletedIds(prev => Array.from(new Set([...prev, record.id as number])));
    setTableData(prev => prev.filter(item => item.rowKey !== record.rowKey));
  };

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setTableData(cloneRows(originRows));
    setDeletedIds([]);
    setCurrentRowId(undefined);
    setCurrentSelectedDevices([]);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (tableData.some(item => item.stationId === undefined)) {
      message.warning('请先为每条关联关系选择站房');
      return;
    }

    const linkDetail = tableData.map(item => ({
      id: item.id,
      stationId: item.stationId as number,
      valveIds: item.valveIds,
    }));

    try {
      setSaveLoading(true);
      if (deletedIds.length > 0) {
        await Promise.all(deletedIds.map(id => apiV2AreaStationValveDelPost({ id })));
      }
      if (linkDetail.length > 0) {
        await apiV2AreaStationValveLinkPost({ linkDetail });
      }
      message.success('保存成功');
      setIsEditing(false);
      await refreshTable();
    } finally {
      setSaveLoading(false);
    }
  };

  const columns = [
    {
      title: '站房',
      dataIndex: 'stationId',
      ellipsis: true,
      render: (value: number | undefined, record: RelationRow) => (
        <Select
          showSearch
          allowClear
          optionFilterProp="label"
          options={stationOptions}
          value={value}
          disabled={!isEditing}
          placeholder="请选择站房"
          style={{ width: 300 }}
          onChange={nextValue => handleStationChange(record.rowKey, nextValue)}
        />
      ),
    },
    {
      title: '关联阀门',
      dataIndex: 'valveIds',
      ellipsis: true,
      render: (valveIds: number[], record: RelationRow) => {
        if (!valveIds?.length) return <EllipsisSpan value="--" />;
        const names = (
          record.valveNames?.length ? record.valveNames : valveIds.map(id => valveNameMap.get(id) ?? id)
        ).join('、');
        return (
          <div className={styles.valveText}>
            <EllipsisSpan value={names} />
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      ellipsis: true,
      render: (_: unknown, record: RelationRow) => (
        <div className={styles.actionGroup}>
          <Button type="link" disabled={!isEditing} onClick={() => openValveModal(record)}>
            关联阀门
          </Button>
          <PopConfirm
            title="确认删除"
            description="确定要删除这条关联关系吗？"
            disabled={!isEditing}
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" danger disabled={!isEditing}>
              删除
            </Button>
          </PopConfirm>
        </div>
      ),
    },
  ];

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]} className={styles.wrapper}>
      <div className={styles.toolbar}>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} disabled={!isEditing} onClick={handleAddRow}>
            新增关联关系
          </Button>
        </Space>
      </div>
      <Table rowKey="rowKey" columns={columns} dataSource={tableData} pagination={false} loading={listLoading} />
      <Space className="sticky-footer" size={8} style={{ zIndex: 99, paddingLeft: 0 }}>
        {!isEditing ? (
          <>
            <Button type="primary" onClick={handleStartEdit}>
              编辑
            </Button>
            <Button onClick={() => navigate('/energy/usage/gasSchedule')}>返回</Button>
          </>
        ) : (
          <>
            <Button type="primary" loading={saveLoading} onClick={handleSave}>
              保存
            </Button>
            <Button onClick={handleCancelEdit}>取消</Button>
          </>
        )}
      </Space>
      <DeviceSelectModal
        visible={!!currentRowId}
        title="关联阀门"
        showTagFilter
        onCancel={() => {
          setCurrentRowId(undefined);
          setCurrentSelectedDevices([]);
        }}
        onOk={handleDeviceSelectOk}
        disabledDevices={[]}
        selectedDevices={currentSelectedDevices}
      />
    </Wrapper>
  );
};

export default StationValveConfiguration;
